// Tenant domain configuration
// Swap these values when you have a real domain

export const TENANT_CONFIG = {
  // The primary root domain. Subdomains identify tenants.
  rootDomain: 'tryggr.dk',

  // Additional root domains that route identically to rootDomain.
  additionalRootDomains: [],

  // The super-admin portal lives here
  adminSubdomain: 'admin',

  // Local dev: use *.app.localhost (e.g. acme.app.localhost:5173)
  // Vite serves all subdomains of localhost by default
  devRootDomain: 'app.localhost',
  devAdminSubdomain: 'admin.app.localhost',
};

// All known production root domains (primary + additional)
export const ALL_ROOT_DOMAINS = [TENANT_CONFIG.rootDomain, ...TENANT_CONFIG.additionalRootDomains];

/**
 * Returns branding for the current domain.
 * Used to white-label the UI when accessed via an additional domain (e.g. tryggr.dk).
 */
export function getDomainBranding() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isTryggr = hostname === 'tryggr.dk' || hostname.endsWith('.tryggr.dk');

  if (isTryggr) {
    return {
      name: 'Tryggr',
      initial: 'T',
      // Tryggr rune — ψ-shaped path from the landing page logo
      logoSvgPath:
        'M 20 0 L 50 42 L 80 0 L 100 4 L 56 48 L 56 108 L 50 128 L 44 108 L 44 48 L 0 4 Z',
      logoViewBox: '0 0 100 128',
    };
  }

  return {
    name: 'SeaSuite',
    initial: 'S',
    logoSvgPath: null,
    logoViewBox: null,
  };
}

/**
 * Detect which known root domain the given hostname belongs to.
 * Returns the matching root domain string, or null if none match.
 */
function detectRootDomain(hostname) {
  for (const domain of ALL_ROOT_DOMAINS) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return domain;
    }
  }
  return null;
}

/**
 * Read the current subdomain from the browser hostname.
 * Returns: { tenantSlug, isSuperAdmin, isValid }
 *
 * Works for all configured root domains (seasuite.dk, tryggr.dk, etc.)
 *
 * Examples:
 *   acme.seasuite.dk            → { tenantSlug: 'acme',  isSuperAdmin: false, isValid: true }
 *   acme.tryggr.dk              → { tenantSlug: 'acme',  isSuperAdmin: false, isValid: true }
 *   admin.seasuite.dk           → { tenantSlug: null,    isSuperAdmin: true,  isValid: true }
 *   admin.tryggr.dk             → { tenantSlug: null,    isSuperAdmin: true,  isValid: true }
 *   seasuite.dk                 → { tenantSlug: null,    isSuperAdmin: false, isValid: false }
 *   mydev.seasuite.dk           → dev fallback (__dev__ tenant login)
 *   mydev.seasuite.dk?tenant=x  → { tenantSlug: 'x',     isSuperAdmin: false, isValid: true }
 *   mytest.seasuite.dk          → dev fallback (__dev__ tenant login)
 *   acme.app.localhost:5173     → { tenantSlug: 'acme',  isSuperAdmin: false, isValid: true }
 *   admin.app.localhost         → { tenantSlug: null,    isSuperAdmin: true,  isValid: true }
 *   localhost:5173              → dev fallback: first available tenant or 'demo'
 */
export function resolveTenant() {
  const hostname = window.location.hostname.toLowerCase();
  const { adminSubdomain, devRootDomain, devAdminSubdomain } = TENANT_CONFIG;

  // ── Production: *.seasuite.dk / *.tryggr.dk (or any configured domain) ──
  const rootDomain = detectRootDomain(hostname);
  if (rootDomain) {
    const sub = hostname === rootDomain ? '' : hostname.replace(`.${rootDomain}`, '');

    // ── Environment portals: mydev.* / mytest.* ──
    // Each is served by its own SWA (routed via Cloudflare Worker).
    // Without ?tenant= param → falls back to __dev__ (tenant login screen).
    // Use ?tenant=slug to access a specific tenant, ?tenant=admin for super-admin.
    const envSubdomains = new Set(['mydev', 'mytest']);
    if (sub && envSubdomains.has(sub)) {
      const params = new URLSearchParams(window.location.search);
      const tenant = params.get('tenant');
      if (tenant === 'admin') return { tenantSlug: null, isSuperAdmin: true, isValid: true };
      if (tenant) return { tenantSlug: tenant, isSuperAdmin: false, isValid: true };
      // No ?tenant= param — resolve to the env's real active tenant (see
      // useTenant.js). isEnvPortal marks the mydev/mytest portals so the
      // resolver prefers the actual active tenant over the synthetic __dev__
      // placeholder, which has no real users (logging into it yields
      // "Invalid credentials"). Falls back to __dev__ only when no real
      // tenant exists.
      return {
        tenantSlug: '__dev__',
        isSuperAdmin: false,
        isValid: true,
        isDevFallback: true,
        isEnvPortal: true,
      };
    }

    if (!sub) return { tenantSlug: null, isSuperAdmin: false, isValid: false };
    if (sub === adminSubdomain) return { tenantSlug: null, isSuperAdmin: true, isValid: true };
    return { tenantSlug: sub, isSuperAdmin: false, isValid: true };
  }

  // ── Dev: *.app.localhost ──
  if (hostname === devRootDomain || hostname.endsWith(`.${devRootDomain}`)) {
    const sub = hostname === devRootDomain ? '' : hostname.replace(`.${devRootDomain}`, '');
    if (!sub) return { tenantSlug: null, isSuperAdmin: false, isValid: false };
    if (hostname === devAdminSubdomain || sub === 'admin')
      return { tenantSlug: null, isSuperAdmin: true, isValid: true };
    return { tenantSlug: sub, isSuperAdmin: false, isValid: true };
  }

  // ── Plain localhost fallback (for simple `npm run dev` without subdomain) ──
  // Return a special dev mode so developers can still work without subdomain setup
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { tenantSlug: '__dev__', isSuperAdmin: false, isValid: true, isDevFallback: true };
  }

  // ── Azure Static Web Apps fallback (for Silo C testing) ──
  // On *.azurestaticapps.net URLs, use ?tenant=slug query parameter
  if (hostname.endsWith('.azurestaticapps.net')) {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('tenant');
    if (slug === 'admin') return { tenantSlug: null, isSuperAdmin: true, isValid: true };
    if (slug) return { tenantSlug: slug, isSuperAdmin: false, isValid: true };
    return { tenantSlug: '__dev__', isSuperAdmin: false, isValid: true, isDevFallback: true };
  }

  // ── Unknown hostname — treat as a potential distributor custom domain ──────
  // Distributors point their own domain (e.g. compliance.acme.dk) to this SWA
  // via CNAME. Tenant resolution falls back to a registry lookup by customDomain.
  return { tenantSlug: null, isSuperAdmin: false, isValid: true, isCustomDomain: true, hostname };
}

/**
 * Build the full URL for a tenant subdomain.
 * Uses the current domain so links stay on whichever domain the user is browsing.
 */
export function tenantUrl(slug) {
  const isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('app.localhost');
  if (isDev) return `http://${slug}.app.localhost:${window.location.port}`;
  const hostname = window.location.hostname.toLowerCase();
  const rootDomain = detectRootDomain(hostname) || TENANT_CONFIG.rootDomain;
  // Environment portals (mydev.*/mytest.*): return the portal URL itself.
  // Only the env subdomain routes to the correct SWA via Cloudflare,
  // and the dev fallback resolves the tenant automatically.
  const sub = hostname === rootDomain ? '' : hostname.replace(`.${rootDomain}`, '');
  if (sub === 'mydev' || sub === 'mytest') {
    return `https://${sub}.${rootDomain}`;
  }
  return `https://${slug}.${rootDomain}`;
}

/**
 * The URL a user should be sent to in order to log in.
 *
 * On production tryggr.dk this is the central login page (login.tryggr.dk):
 * the user enters their username and the page resolves their tenant for them,
 * so they never need to know their own slug. Falls back to the tenant
 * subdomain on environment portals (mydev/mytest), custom/distributor domains,
 * and local dev — surfaces where central login does not run.
 */
export function loginEntryUrl(slug) {
  const isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('app.localhost');
  if (isDev) return tenantUrl(slug);

  const hostname = window.location.hostname.toLowerCase();
  const rootDomain = detectRootDomain(hostname);
  // Custom / distributor domains have no central login — keep the tenant URL.
  if (!rootDomain) return tenantUrl(slug);

  const sub = hostname === rootDomain ? '' : hostname.replace(`.${rootDomain}`, '');
  // Environment portals (mydev/mytest) have no central login.
  if (sub === 'mydev' || sub === 'mytest') return tenantUrl(slug);

  // Production: central login resolves the tenant from the username.
  return `https://login.${rootDomain}`;
}

/**
 * Build the correct URL for a tenant in a specific environment.
 * Production tenants use subdomains; test/dev use the env portal with ?tenant=slug.
 * Uses the current domain so links stay on whichever domain the user is browsing.
 */
export function tenantUrlForEnv(slug, env) {
  const rootDomain =
    detectRootDomain(window.location.hostname.toLowerCase()) || TENANT_CONFIG.rootDomain;
  if (env === 'test')
    return slug === 'mytest'
      ? `https://mytest.${rootDomain}`
      : `https://mytest.${rootDomain}?tenant=${slug}`;
  if (env === 'development')
    return slug === 'mydev'
      ? `https://mydev.${rootDomain}`
      : `https://mydev.${rootDomain}?tenant=${slug}`;
  return `https://${slug}.${rootDomain}`;
}

/**
 * Build the super-admin URL.
 * Uses the current domain so links stay on whichever domain the user is browsing.
 */
export function adminUrl() {
  const isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('app.localhost');
  if (isDev) return `http://admin.app.localhost:${window.location.port}`;
  const rootDomain =
    detectRootDomain(window.location.hostname.toLowerCase()) || TENANT_CONFIG.rootDomain;
  return `https://${TENANT_CONFIG.adminSubdomain}.${rootDomain}`;
}
